/**
 * AI-assisted answer selection for the TransCelerate Patient Library
 * Section 5.4.1/5.4.2 decision tree, sourced from an uploaded/fetched USDM
 * catalogue.
 *
 * IMPORTANT — scope of what this does and does not do:
 * - This ONLY selects which decision-tree answer applies (participant
 *   population pathway, genotoxicity risk, female/male risk classification).
 * - It never generates or rewrites the Patient Library text itself. Once the
 *   decisions below are applied, patientLibrarySection54.js's deterministic
 *   assemblePatientLibrarySection54() still produces the verbatim Section
 *   5.4.1/5.4.2 text exactly as if a person had picked the same answers by
 *   hand — see usdmTemplateFieldGeneration.js's UNSUPPORTED_USDM_TEMPLATE_FIELDS
 *   comment for why the TEXT stays permanently out of LLM-generation scope.
 *
 * USDM has no structured field for genotoxicity/teratogenicity risk — that's
 * a nonclinical safety classification, not trial-design data — so this reads
 * whatever narrative facts are available (population/eligibility text,
 * indication, interventions) and makes a best-effort classification from
 * them. Ambiguous cases are resolved toward the MORE conservative (more
 * protective) contraception classification rather than left blank, since
 * this runs with no required human confirmation step.
 */

import { generateContent } from '../../services/AIService';
import { buildUsdmCatalogueGlobalContext } from './usdmCatalogueContext';
import {
  DEFAULT_PATIENT_LIBRARY_DECISIONS,
  CHILD_BEARING_PATHWAY,
  GENOTOXICITY_RISK,
  FEMALE_REPRODUCTIVE_RISK,
  MALE_EJACULATE_RISK,
} from '../../utils/patientLibrarySection54';

const VALID_VALUES = {
  childbearing_population_pathway: Object.values(CHILD_BEARING_PATHWAY),
  genotoxicity_risk: Object.values(GENOTOXICITY_RISK),
  female_reproductive_risk: Object.values(FEMALE_REPRODUCTIVE_RISK),
  male_ejaculate_risk: Object.values(MALE_EJACULATE_RISK),
};

function stripJsonFences(raw) {
  let cleaned = String(raw || '').trim();
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(json)?\s*/i, '').replace(/\s*```$/, '');
  }
  return cleaned.trim();
}

function buildPrompt() {
  return `ROLE: You are classifying a clinical trial into the TransCelerate Patient Library Section 5.4.1/5.4.2 contraception decision tree, using only the study facts provided below.

Answer these questions strictly from the study facts. This decision drives which verbatim Patient Library contraception language is inserted into the protocol, so:
- Always give a non-empty best-effort answer for "childbearing_population_pathway" and "genotoxicity_risk" even if the facts are incomplete.
- When evidence is ambiguous or absent, resolve toward the MORE conservative / more protective classification (e.g., prefer "cbp" over "ncbp_only" unless eligibility explicitly restricts to non-childbearing-potential participants; prefer the higher-risk female/male classification when unsure) rather than leaving a field blank.
- Only report "has_study_specific_reproductive_requirements": true if the facts state a concrete additional reproductive-safety requirement (e.g., a specific post-dose contraception duration, pregnancy-testing cadence, sperm/egg donation restriction) beyond the standard Patient Library language; otherwise false.

QUESTIONS
1) childbearing_population_pathway — "cbp" (the study enrolls, or does not explicitly exclude, participants of childbearing potential) or "ncbp_only" (eligibility explicitly restricts enrollment to non-childbearing-potential participants only — e.g., postmenopausal, surgically sterile, males with documented vasectomy, pediatric-only, etc.).
2) genotoxicity_risk — "yes" or "no": does the investigational product have known/suspected clinically relevant genotoxic potential per the study facts (e.g., cytotoxic chemotherapy, alkylating agents, radiopharmaceuticals, or explicit genotoxicity statements)?
3) female_reproductive_risk — only when genotoxicity_risk is "no": "demonstrated_suspected" (demonstrated or suspected human teratogenicity/fetotoxicity), "possible" (possible teratogenicity/fetotoxicity — mechanism-based concern, e.g. most oncology small molecules/biologics without human data), or "unlikely" (unlikely teratogenicity/fetotoxicity, e.g. large molecules with no placental transfer evidence and no mechanism-based concern).
4) male_ejaculate_risk — only when genotoxicity_risk is "no": "risk_via_ejaculate" or "no_risk_via_ejaculate".
5) has_study_specific_reproductive_requirements (boolean) and study_specific_reproductive_requirements (string, "" if false).

Return ONLY a single JSON object, no markdown fences, no commentary, with exactly these keys:
{
  "childbearing_population_pathway": "cbp" | "ncbp_only",
  "genotoxicity_risk": "yes" | "no",
  "female_reproductive_risk": "demonstrated_suspected" | "possible" | "unlikely" | "",
  "male_ejaculate_risk": "risk_via_ejaculate" | "no_risk_via_ejaculate" | "",
  "has_study_specific_reproductive_requirements": true | false,
  "study_specific_reproductive_requirements": "string"
}`;
}

/**
 * @param {object} usdmFacts - normalizeUsdmCatalogue()'s output
 * @returns {Promise<{ok:true, decisions:object} | {ok:false, message:string}>}
 */
export async function inferPatientLibraryDecisionsFromUsdm(usdmFacts) {
  const usdmContext = buildUsdmCatalogueGlobalContext(usdmFacts);
  if (!usdmContext) {
    return { ok: false, message: 'No USDM catalogue data available to classify from.' };
  }

  let raw;
  try {
    raw = await generateContent(buildPrompt(), usdmContext, '', null, null, null);
  } catch (err) {
    return { ok: false, message: err.message || 'Failed to reach AI service.' };
  }

  let parsed;
  try {
    parsed = JSON.parse(stripJsonFences(raw));
  } catch (err) {
    return { ok: false, message: 'AI did not return valid JSON for the Patient Library decision tree.' };
  }

  const decisions = { ...DEFAULT_PATIENT_LIBRARY_DECISIONS };
  for (const key of Object.keys(VALID_VALUES)) {
    if (VALID_VALUES[key].includes(parsed?.[key])) decisions[key] = parsed[key];
  }
  decisions.has_study_specific_reproductive_requirements = Boolean(
    parsed?.has_study_specific_reproductive_requirements
  );
  decisions.study_specific_reproductive_requirements = decisions.has_study_specific_reproductive_requirements
    ? String(parsed?.study_specific_reproductive_requirements || '').trim()
    : '';

  if (!decisions.childbearing_population_pathway || !decisions.genotoxicity_risk) {
    return {
      ok: false,
      message: 'AI could not determine a participant population / genotoxicity risk classification from the uploaded USDM.',
    };
  }

  return { ok: true, decisions };
}
